import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { api } from '../../utils';
import { routes } from '../../lib/routes';
import { Spinner } from '../../components/common';

/**
 * V1.5.M10 B-P2 — white-label subdomain landing for the OAuth relay.
 *
 * The canonical origin redirected here with a single-use handback code (?hc=)
 * after the user signed in with Google. We redeem it for THIS subdomain's
 * session (a fresh JWT), store it, and drop the user into the normal post-login
 * flow. The pending-invite claim effect in AuthContext fires automatically once
 * the session is live.
 */
const OAuthRelayPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkStatus } = useAuth();
  const { name } = useBranding();
  const [failed, setFailed] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // exchange exactly once (codes are single-use)
    ran.current = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const hc = params.get('hc');
      const invite = params.get('invite');
      // Preserve the invite so the standard post-auth claim effect redeems it.
      if (invite) localStorage.setItem('pending-invite', invite);
      if (!hc) {
        setFailed(true);
        return;
      }
      try {
        const res = await api.post('/auth/relay/exchange', { code: hc });
        const token = res?.data?.result?.access_token as string | undefined;
        if (!token) throw new Error('relay exchange returned no token');
        localStorage.setItem('auth-token', token);
        // Strip the one-time code from the URL/history immediately.
        window.history.replaceState({}, '', window.location.pathname);
        const userData = await checkStatus();
        if (userData && userData.onboarding_status !== 'completed') {
          navigate(routes.onboardingChat, { replace: true });
        } else {
          navigate(routes.home, { replace: true });
        }
      } catch {
        setFailed(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center px-6 text-center'>
        <h1 className='text-2xl font-heading font-medium text-secondary-500 mb-2'>
          This sign-in link expired
        </h1>
        <p className='text-body-500 mb-6 max-w-md'>
          Sign-in links are single-use and only valid for a minute. Please start
          again from your event link.
        </p>
        <button
          onClick={() => navigate(routes.login, { replace: true })}
          className='text-primary-500 font-semibold'
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col items-center justify-center gap-4'>
      <Spinner size='md' color='primary' />
      <p className='text-body-500'>Signing you in to {name}...</p>
    </div>
  );
};

export default OAuthRelayPage;
